import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { candidateService } from '../../services/api/candidateService';
import { jobService } from '../../services/api/jobService';
import { stageService } from '../../services/api/stageService';
import {
    Candidate,
    Job,
    Stage,
    CandidateCreateRequest,
    CandidateUpdateRequest
} from '../../types/types';

interface CandidateFormProps {
    candidate?: Candidate;
    onSave: (candidate: Candidate) => void;
    onCancel: () => void;
}

const CandidateForm: React.FC<CandidateFormProps> = ({
    candidate,
    onSave,
    onCancel
}) => {
    const isEditMode = !!candidate;

    // Form state
    const [formData, setFormData] = useState<CandidateCreateRequest | CandidateUpdateRequest>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        jobId: '',
        stageId: '',
        source: '',
        notes: ''
    });

    // Resources state
    const [jobs, setJobs] = useState<Job[]>([]);
    const [stages, setStages] = useState<Stage[]>([]);

    // Loading states
    const [jobsLoading, setJobsLoading] = useState<boolean>(true);
    const [stagesLoading, setStagesLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);

    // Validation state
    const [validated, setValidated] = useState<boolean>(false);

    // Load jobs and stages
    useEffect(() => {
        const fetchResources = async () => {
            try {
                setJobsLoading(true);
                setStagesLoading(true);

                const [jobsData, stagesData] = await Promise.all([
                    jobService.getJobs(),
                    stageService.getActiveStages()
                ]);

                // Filter active jobs and sort them
                const activeJobs = jobsData.items
                    .filter(job => job.isActive)
                    .sort((a, b) => a.title.localeCompare(b.title));
                setJobs(activeJobs);

                // Filter active stages and sort by display order
                const activeStages = stagesData
                    .filter(stage => stage.isActive)
                    .sort((a, b) => a.displayOrder - b.displayOrder);
                setStages(activeStages);

                // Set default stage if creating a new candidate
                if (!isEditMode && activeStages.length > 0) {
                    // Find the "New" stage or use the first one
                    const newStage = activeStages.find(s => s.name.toLowerCase() === 'new') || activeStages[0];
                    setFormData(prev => ({ ...prev, stageId: newStage.id }));
                }
            } catch (error) {
                console.error('Error fetching resources:', error);
            } finally {
                setJobsLoading(false);
                setStagesLoading(false);
            }
        };

        fetchResources();
    }, [isEditMode]);

    // Initialize form with candidate data if in edit mode
    useEffect(() => {
        if (candidate) {
            setFormData({
                id: candidate.id,
                firstName: candidate.firstName,
                lastName: candidate.lastName,
                email: candidate.email,
                phone: candidate.phone || '',
                jobId: candidate.jobId || '',
                stageId: candidate.stageId,
                source: candidate.source || '',
                notes: candidate.notes || ''
            });
        }
    }, [candidate]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const form = e.currentTarget;
        if (!form.checkValidity()) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        setSaving(true);
        try {
            let savedCandidate: Candidate;

            if (isEditMode && candidate) {
                // Update existing candidate
                savedCandidate = await candidateService.updateCandidate(candidate.id, formData as CandidateUpdateRequest);
            } else {
                // Create new candidate
                savedCandidate = await candidateService.createCandidate(formData as CandidateCreateRequest);
            }

            onSave(savedCandidate);
        } catch (error) {
            console.error('Error saving candidate:', error);
            alert('Failed to save candidate. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                        />
                        <Form.Control.Feedback type="invalid">
                            First name is required.
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                        />
                        <Form.Control.Feedback type="invalid">
                            Last name is required.
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                        />
                        <Form.Control.Feedback type="invalid">
                            Please provide a valid email.
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Phone (Optional)</Form.Label>
                        <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Job</Form.Label>
                        <Form.Select
                            name="jobId"
                            value={formData.jobId}
                            onChange={handleInputChange}
                            required
                            disabled={jobsLoading}
                        >
                            <option value="">Select a job...</option>
                            {jobs.map(job => (
                                <option key={job.id} value={job.id}>
                                    {job.title}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            Please select a job.
                        </Form.Control.Feedback>
                        {jobsLoading && (
                            <div className="text-muted mt-1">
                                <Spinner animation="border" size="sm" /> Loading jobs...
                            </div>
                        )}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Stage</Form.Label>
                        <Form.Select
                            name="stageId"
                            value={formData.stageId}
                            onChange={handleInputChange}
                            required
                            disabled={stagesLoading}
                        >
                            <option value="">Select a stage...</option>
                            {stages.map(stage => (
                                <option key={stage.id} value={stage.id}>
                                    {stage.name}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            Please select a stage.
                        </Form.Control.Feedback>
                        {stagesLoading && (
                            <div className="text-muted mt-1">
                                <Spinner animation="border" size="sm" /> Loading stages...
                            </div>
                        )}
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Source (Optional)</Form.Label>
                <Form.Control
                    type="text"
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    placeholder="e.g., LinkedIn, Referral, Indeed"
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Notes (Optional)</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Any additional information about this candidate"
                />
            </Form.Group>

            <div className="d-flex justify-content-end">
                <Button variant="outline-secondary" onClick={onCancel} className="me-2">
                    Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={saving}>
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {isEditMode ? 'Updating...' : 'Creating...'}
                        </>
                    ) : (
                        isEditMode ? 'Update Candidate' : 'Create Candidate'
                    )}
                </Button>
            </div>
        </Form>
    );
};

export default CandidateForm;